/** @format */

import dayjs from "dayjs";
import Linkify from "linkify-react";
import React, { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRetryIncompleteData } from "senf-atomic-design-system";
import {
  fetchTranslation,
  getPhaseOfDateRange,
  getUserHandle,
  openLink,
  openMail,
} from "senf-shared";
import i18n from "senf-shared/utils/i18n";
import styled from "styled-components";
import { useAuthModal } from "../../../hooks";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import theme from "../../../styles/theme";
import { cn } from "../../../util/classNameConstructor";
import Transition from "../../atoms/animations/Transition";
import Box from "../../atoms/box/Box";
import Input from "../../atoms/inputs/Input";
import Skeleton from "../../atoms/skeleton/Skeleton";
import Typography from "../../atoms/typography/Typography";
import Avatar from "../../core/avatar/Avatar";
import Button from "../../core/button/Button";
import Dropdown from "../../core/dropdown/Dropdown";
import Icon from "../../core/icon/Icon";
import Seperator from "../../core/seperator/Seperator";
import CommentCard from "../../molecules/cards/CommentCard";
import { locales } from "../../molecules/languageSettings/LanguageSettings";
import List from "../../molecules/list/List";
import { useModals } from "../../molecules/modalStack/ModalProvider";
import { useSocialLinks } from "../../organisms/socialmediaShare/SocialmediaShare";
import type { IdeaDetailTemplateProps } from "./MonitoringIdeaDetailTemplate.types";

const ScrollBumper = styled.div`
  position: sticky;
  top: -10px;

  .isSticky {
    position: fixed;
    top: 10px;
    z-index: 9999;
  }
`;
const IdeaDetailTemplate: FC<IdeaDetailTemplateProps> = ({
  ideaMap,
  mode,
  data,
  user,
  commentFormInput,
  setCommentFormInput,
  commentFormLoading,
  catchRef,
  project,
}) => {
  const {
    handle,
    ideaId,
    title,
    body,
    weblink,
    weblinkTitle,
    contact,
    contactTitle,
    likeCount,
    commentCount,
    selectedUnix,
    userHandle,
    isAnonymous,
    userImage,
    userId,
    createdAt,
    comments,
    translations,
    lang,
    topic,
  } = data;
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [loadingLike, setLoadingLike] = useState(false);

  const { openAuthModal, handleCheckAuth } = useAuthModal(project);
  const [localLiked, setLocalLiked] = useState(null as any);

  useRetryIncompleteData({
    data: { title, topic, ideaId },
    refetchData: handle?.refetchData,
  });

  const liked = useMemo(() => {
    // Check if the user's likes have changed since we set local state
    const currentUserLiked = user?.likes?.find(
      (like) => like.ideaId === ideaId,
    );

    // If local state exists but doesn't match current user state, reset it
    if (localLiked !== null && localLiked !== currentUserLiked) {
      setLocalLiked(null);
      return currentUserLiked;
    }

    // Use local state if available, otherwise fall back to user.likes
    if (localLiked !== null) {
      return localLiked;
    }
    return currentUserLiked;
  }, [user?.likes, ideaId, localLiked]);

  const { socialLinks } = useSocialLinks();

  const commented = useMemo(() => {
    return user?.comments?.find((comment) => comment.ideaId === ideaId);
  }, [user?.comments, ideaId]);

  let selectedDates = [];
  const selectedUnixArray = selectedUnix;
  const options = {
    weekday: "short",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };

  if (selectedUnixArray !== undefined && selectedUnixArray !== null) {
    if (selectedUnixArray.length > 0) {
      selectedUnixArray.forEach((element) => {
        selectedDates.push(
          <div key={element * 1000}>
            {new Date(element * 1000).toLocaleTimeString("de-DE", options)}{" "}
            <br />{" "}
          </div>,
        );
      });
    } else {
      selectedDates = (
        <div>
          {new Date(selectedUnix * 1000).toLocaleTimeString("de-DE", options)}{" "}
          <br />{" "}
        </div>
      );
    }
  }

  const handleLike = (event) => {
    setLoadingLike(true);
    handle
      ?.buttonLike(event, ideaId)
      .then((result) => {
        // Update local like state based on the result
        // If result is true, the idea was liked; if false, it was unliked
        setLocalLiked(result ? { ideaId } : null);
      })
      .finally(() => setLoadingLike(false))
      .catch(() => setLoadingLike(false));
  };

  const withinDateRange =
    getPhaseOfDateRange(ideaMap?.startDate, ideaMap?.endDate) === "while";

  const [translatedTitle, setTranslatedTitle] = useState(null);
  const [translatedDescription, setTranslatedDescription] = useState(null);

  const handleTranslateDescription = () => {
    fetchTranslation(project, data, "body")
      .then((translated) => {
        setTranslatedDescription(translated); // Set the translation once it's resolved
      })
      .catch((error) => {
        console.error("Error fetching translation:", error);
        // Handle any errors, e.g., set a fallback translation or UI state
        setTranslatedDescription(body); // Fallback to the original title on error
      });

    fetchTranslation(project, data)
      .then((translated) => {
        setTranslatedTitle(translated); // Set the translation once it's resolved
      })
      .catch((error) => {
        console.error("Error fetching translation:", error);
        // Handle any errors, e.g., set a fallback translation or UI state
        setTranslatedTitle(title); // Fallback to the original title on error
      });
  };

  return (
    <div className="px-5">
      {project?.settings?.originalLanguage &&
        i18n.resolvedLanguage !== project?.settings?.originalLanguage &&
        lang !== i18n.resolvedLanguage && (
          <Button
            text={
              translatedTitle
                ? t("util:show", { val: t("global:original") })
                : t("util:show", { val: t("global:translation") })
            }
            size="sm"
            variant="outline"
            leadingIcon={
              locales[
                translatedTitle
                  ? project.settings.originalLanguage
                  : i18n.resolvedLanguage
              ].icon
            }
            classNameWrapper="mb-3"
            onClick={() => {
              if (!translatedTitle) {
                handleTranslateDescription();
              } else {
                setTranslatedTitle(null);
                setTranslatedDescription(null);
              }
            }}
          />
        )}
      <Typography variant="h3" style={{ wordBreak: "break-word" }}>
        {translatedTitle ||
          translations?.[i18n.resolvedLanguage]?.title ||
          title || <Skeleton />}
      </Typography>
      <div
        className="my-2 flex items-start"
        style={{ wordBreak: "break-word" }}
        id="IdeaDetailTemplateScrollWrapper"
      >
        <ScrollBumper id="scrollBumpeRef" ref={catchRef} />
        <Typography variant="bodyBg">
          {body ? (
            <Linkify>
              {translatedDescription ||
                translations?.[i18n.resolvedLanguage]?.body ||
                body}
            </Linkify>
          ) : (
            <Skeleton count={4} width={300} />
          )}
        </Typography>
      </div>

      <div className="my-4 flex items-center gap-3">
        <Avatar
          alt={getUserHandle(isAnonymous, userId, userHandle)}
          size="sm"
          src={userImage}
          onClick={
            userId !== null ? () => handle?.openProfilePage(userId) : undefined
          }
        />

        {createdAt && (
          <div className="flex gap-1.5">
            <Typography
              variant="buttonSm"
              onClick={() => userId !== null && handle?.openProfilePage(userId)}
              style={{ cursor: userHandle !== null ? "pointer" : "default" }}
            >
              {!userId
                ? t("global:deletedUser")
                : getUserHandle(isAnonymous, userId, userHandle)}
            </Typography>
            <Typography
              variant="buttonSm"
              color={theme.colors.black.black40tra}
            >
              {t("global:at")}
            </Typography>
            <Typography
              variant="buttonSm"
              color={theme.colors.black.black40tra}
            >
              {dayjs(createdAt).format("DD.MM.YYYY")}
            </Typography>
          </div>
        )}
      </div>

      <div className="-mx-5 my-5">
        <Seperator orientation="horizontal" />
      </div>

      {(weblink || contact) && (
        <div className="mb-4 mt-3 flex gap-2 flex-wrap">
          {contact && (
            <Button
              variant="shapeless"
              leadingIcon="mail"
              text={contactTitle || contact}
              onClick={() => openMail(contact)}
            />
          )}
          {weblink && (
            <Button
              variant="shapeless"
              leadingIcon="link"
              text={weblinkTitle || weblink}
              onClick={() => openLink(weblink)}
            />
          )}
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <Box
          gap="8px"
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(100px, 1fr))"
        >
          <Dropdown
            size="md"
            width="max"
            className="mx-auto"
            leadingIcon="share"
            text={t("global:share")}
            trailingIcon={false}
            items={socialLinks({
              trackLocation: "ideaDetailPage",
              // path: `${window.location.origin}/projects/${project.projectId}/${data.ideaId}`,
            })}
            disabled={mode === "monitoring"}
            disabledNotice={
              mode === "monitoring"
                ? t("global:interactionNotPossibleAsModerator")
                : undefined
            }
          />

          {ideaMap?.interactionOptions?.allowLikes && (
            <Button
              disabled={
                mode === "monitoring" || !withinDateRange || loadingLike
              }
              disabledNotice={
                mode === "monitoring"
                  ? t("global:interactionNotPossibleAsModerator")
                  : !withinDateRange
                    ? t("global:interactionNotPossibleAnymore")
                    : undefined
              }
              width="max"
              leadingIcon={liked ? "heart-fill" : "heart"}
              leadingIconProps={{
                color: liked ? theme.colors.palette.alert : "current",
              }}
              // textSubdued={!liked}
              text={<span>{likeCount}</span>}
              onClick={handleLike}
              // style={
              //   loadingLike
              //     ? { pointerEvents: "none", cursor: "default", opacity: 0.6 }
              //     : {}
              // }
            />
          )}

          {ideaMap?.interactionOptions?.allowComments && (
            <Button
              width="max"
              disabled={mode !== "monitoring" && !withinDateRange}
              disabledNotice={
                !withinDateRange && t("global:interactionNotPossibleAnymore")
              }
              leadingIcon={commented ? "comment-fill" : "comment"}
              // textSubdued={!commented}
              text={<span>{commentCount}</span>}
              onClick={(event) => {
                handle?.buttonComment(event, ideaId);
              }}
            />
          )}
        </Box>
      </div>

      <div className="-mx-5 my-5">
        <Seperator orientation="horizontal" />
      </div>

      {ideaMap?.interactionOptions?.allowComments &&
        (withinDateRange || comments?.length > 0) && (
          <div className="mb-3.5 mt-2.5 flex items-center gap-3">
            <Icon
              name="chat-duo"
              className={cn(
                " scale-[150%]",
                document.dir === "rtl" ? "mr-1" : "ml-1",
              )}
            />
            <h3 className="heading-xl font-black">{t("global:discussion")}</h3>
          </div>
        )}

      {(mode === "monitoring" || withinDateRange) && (
        <div className="my-5 flex flex-col gap-2.5">
          {!user?.userId ? (
            <Button
              variant="primary"
              size="lg"
              width="max"
              onClick={() => openAuthModal("login", { variant: "comment" })}
              text={t("auth:login")}
            />
          ) : (
            <div className="w-full">
              {(ideaMap?.interactionOptions?.allowComments ||
                mode === "monitoring") && (
                <Input
                  placeholder={t("dialogMap:detailPage.commentPlaceholder")}
                  value={commentFormInput}
                  onChange={(event) => setCommentFormInput(event.target.value)}
                  name="ideaDetailPage.commentInput"
                  type="textarea"
                  onFocus={handleCheckAuth}
                  rows={1}
                  maxRows={5}
                />
              )}

              <Transition
                show={commentFormInput?.length > 0}
                from={{ x: 0, y: -10, scale: 0.85, height: 0 }}
                leave={{ x: 0, y: -10, scale: 0.85, height: 0 }}
                enter={{ x: 0, y: 0, scale: 1, height: 50 }}
                style={{
                  width: "100%",
                  position: "relative",
                }}
              >
                <Button
                  width="max"
                  variant="primary"
                  size="lg"
                  leadingIcon="Paperplane"
                  text={t("global:send")}
                  disabled={commentFormInput === "" || commentFormLoading}
                  loading={commentFormLoading}
                  onClick={handle?.submitComment}
                  style={{
                    marginTop: "8px",
                  }}
                />
              </Transition>
            </div>
          )}
        </div>
      )}
      {comments && (
        <List
          data={comments}
          CardType={CommentCard}
          project={project}
          listEndText={" "}
          handle={handle}
          user={user}
          shouldTranslate={translatedTitle}
        />
      )}
    </div>
  );
};

export default IdeaDetailTemplate;
